import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { AuthUser } from '@/features/auth/types/auth.types'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: AuthUser | null
  isAuthenticated: boolean
  /**
   * Duplicado do `user.mustChangePassword` para ficar disponível assim que o
   * login/refresh responde — antes mesmo de `me()` completar e popular `user`.
   * Isso evita uma janela em que um guard de rota (RF06) ainda não sabe que
   * precisa redirecionar para `/change-password`. `setUser` mantém os dois
   * valores em sincronia quando `me()` responde.
   */
  mustChangePassword: boolean
}

interface AuthActions {
  setTokens: (
    accessToken: string,
    refreshToken: string | null,
    mustChangePassword?: boolean,
  ) => void
  setUser: (user: AuthUser) => void
  /**
   * Atualiza `mustChangePassword` fora do fluxo de `setTokens`/`setUser` — uso
   * principal é `use-change-password.ts` zerando o flag após uma troca de
   * senha bem-sucedida, para que `RequirePasswordChangeRoute` (RF06) libere a
   * navegação para as demais rotas autenticadas. Mantém `user.mustChangePassword`
   * em sincronia, já que os dois campos duplicam a mesma informação.
   */
  setMustChangePassword: (mustChangePassword: boolean) => void
  clearAuth: () => void
}

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  isAuthenticated: false,
  mustChangePassword: false,
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set) => ({
      ...initialState,

      setTokens: (accessToken, refreshToken, mustChangePassword) =>
        set((state) => ({
          accessToken,
          refreshToken,
          isAuthenticated: true,
          mustChangePassword: mustChangePassword ?? state.mustChangePassword,
        })),

      setUser: (user) => set({ user, mustChangePassword: user.mustChangePassword }),

      setMustChangePassword: (mustChangePassword) =>
        set((state) => ({
          mustChangePassword,
          user: state.user ? { ...state.user, mustChangePassword } : state.user,
        })),

      clearAuth: () => set(initialState),
    }),
    {
      name: 'navega-auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        mustChangePassword: state.mustChangePassword,
      }),
    },
  ),
)
